/* eslint-disable react/require-default-props */
import { Bill } from '@enview/interface/types/bills/Bill';
import { BillViewConfig } from './BillTable';
import { ReactElement, useMemo } from 'react';
import BillBulkActionsDropdown from '../DropDowns/BillBulkActionsDropdown';
import BillViewDropdown from '../DropDowns/BillViewDropdown';
import MultipleSelectDropDown, {
  OptionType,
} from '../DropDowns/MultipleSelectDropDown';
import BillActionFilterDropdown from '../../LegislativeTracking/SearchFilters/BillActionFilterDropdown';
import {
  DefaultBillTableColumns,
  REQUIRED_COLUMNS,
  SearchActionFilter,
} from '@enview/interface/types/Bill';
import BillStatusFilterDropdown from '../../LegislativeTracking/SearchFilters/BillStatusFilterDropdown';
import PartySponsorFilterDropdown from '../../LegislativeTracking/SearchFilters/PartySponsorFilterDropdown';
import { getStatusOptionsForJurisdictions } from '../../../helpers/BillSearchHelper';
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params';
import MultiSelectFilter from '../Search/MultiSelectFilter';
import ColumnSelectDropdown from '../DropDowns/ColumnSelectDropdown';
import { useOrganizationCustomFields } from '../../../api';
import { useSelector } from 'react-redux';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { buildColumnOptions } from '../../../utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { CustomFieldFilter } from '../BillCustomField/CustomFieldFilter';
import { CustomFieldFilterValue } from '@enview/interface/types/BillSearch';
import featureFlags, { FrontendFeature } from '../../../featureFlags';
import { State } from '../../../dux/@types';
import CloseIcon from '../../../components/svg/CloseIcon';

export type BillTableFilterProperties = {
  filterLabel?: string;
  selectedFilters?: string[];
  filterOptions?: OptionType<string>[];
  setSelectedFilters?: (selection: string[]) => void;
  tagFilterOptions?: OptionType<string>[];
  selectedTags?: string[];
  setSelectedTags?: (tags: string[]) => void;
  actionFilter?: SearchActionFilter | undefined;
  setActionFilter?: (arg: SearchActionFilter | undefined) => void;
  statusFilters?: string[];
  setStatusFilters?: (filters: string[]) => void;
  partySponsorFilters?: string[];
  setPartySponsorFilters?: (filters: string[]) => void;
  selectedTagFilterOption?: string;
  setSelectedTagFilterOption?: (option: string) => void;
  workspaceFilterOptions?: OptionType<string>[];
  sessionFilterOptions?: OptionType<string>[];
  // False hides the filter row (count, filters and Clear All) while keeping the actions
  // row, for pages that show the filters behind their own toggle (e.g. All Bills).
  showFilterRow?: boolean;
};

type BillTableHeaderProps = {
  results: { data: Bill[]; count: number };
  selectedRows: Bill[];
  showFilterProperties: boolean;
  filterProperties?: BillTableFilterProperties;
  query: DecodedValueMap<QueryParamConfigMap>;
  setQuery: SetQuery<QueryParamConfigMap>;
  selectedColumns: string[];
  setSelectedColumns: (a: any) => void;
  billViewConfig?: BillViewConfig;
  extraColumnHeaders?: Partial<Record<DefaultBillTableColumns, string>>;
  // When provided (even as null), replaces the Columns/View controls with this
  // content in the compact (!showFilterProperties) header — used by What's New,
  // whose sections share one Columns button and don't offer a saved-view switcher.
  headerFilterPill?: ReactElement | null;
  showMarkAsReadBulkAction?: boolean;
  clearSelectedRows?: () => void;
};

const BillTableHeader = (props: BillTableHeaderProps): ReactElement => {
  const {
    showFilterProperties,
    filterProperties,
    results,
    selectedRows,
    query,
    setQuery,
    selectedColumns,
    setSelectedColumns,
    billViewConfig,
    extraColumnHeaders,
    headerFilterPill,
    showMarkAsReadBulkAction = false,
    clearSelectedRows,
  } = props;
  const {
    currentView,
    onViewChange,
    readOnly = false,
    editingPublicConfig = false,
  } = billViewConfig ?? {};
  const isPublicView = readOnly || editingPublicConfig;
  const {
    filterLabel = 'Jurisdiction',
    selectedFilters = [],
    filterOptions,
    tagFilterOptions,
    workspaceFilterOptions,
    sessionFilterOptions,
    showFilterRow = true,
  } = filterProperties || {};

  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const isCustomFieldFilterEnabled = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.CustomFieldFilter,
    orgUser,
  );

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const teamId = useSelector(getTeamMode);
  const { data: customFields = [] } = useOrganizationCustomFields(
    orgId ? orgId : skipToken,
  );

  const columnOptions = useMemo(
    () =>
      buildColumnOptions(customFields, false, editingPublicConfig, extraColumnHeaders),
    [customFields, editingPublicConfig, extraColumnHeaders],
  );

  const handleCustomFieldFiltersChange = (value: CustomFieldFilterValue) => {
    setQuery({ customFieldFilter: value }, 'replaceIn');
  };

  const clearAllFilters = () => {
    setQuery(
      {
        ...(filterLabel === 'Jurisdiction' ? { jurisdictions: [] } : {}),
        sessions: [],
        tags: undefined,
        tagFilterMode: 'any',
        status: undefined,
        searchActionType: undefined,
        searchActionFromDate: undefined,
        searchActionToDate: undefined,
        sponsors: undefined,
        workspaceIds: [],
        workspaceFilterMode: undefined,
        customFieldFilter: undefined,
        page: 1,
      },
      'replaceIn',
    );
  };

  const handleColumnChange = (values: string[]) => {
    const withRequired = Array.from(new Set([...values, ...REQUIRED_COLUMNS]));
    setSelectedColumns(withRequired);
  };

  const statusOptions = getStatusOptionsForJurisdictions(selectedFilters);

  const selectedStatuses = useMemo(() => query.status ?? [], [query.status]);
  const selectedSessions = useMemo(
    () =>
      filterLabel === 'Jurisdiction'
        ? query?.jurisdictions ?? []
        : query?.sessions ?? [],
    [filterLabel, query?.jurisdictions, query?.sessions],
  );
  const selectedSponsorFilters = useMemo(() => query.sponsors ?? [], [query.sponsors]);
  const currentActionFilter =
    query.searchActionType && (query.searchActionFromDate || query.searchActionToDate)
      ? {
          type: query.searchActionType,
          fromDate: query.searchActionFromDate ?? undefined,
          toDate: query.searchActionToDate ?? undefined,
        }
      : undefined;

  const getQueryUpdate = (selectedValues: string[]) => {
    if (filterLabel === 'Jurisdiction')
      return {
        jurisdictions: selectedValues,
        page: 1,
      };
    return { sessions: selectedValues, page: 1 };
  };

  if (readOnly) return <></>;

  return (
    <div className="row bill-table-header">
      {showFilterProperties && (
        <div className="jurisdiction-filter">
          {!isPublicView && showFilterRow && (
            <div className="filter-group">
              <div className="total-count">
                <span>
                  {results?.count ?? 0} bill{results && results?.count == 1 ? '' : 's'}
                </span>
              </div>
              {filterOptions && (
                <div className="filter jurisdictions">
                  <MultipleSelectDropDown
                    currentSelectedValues={selectedSessions}
                    floatSelectedToTop
                    onChange={(selectedFilterValues) => {
                      setQuery(getQueryUpdate(selectedFilterValues), 'replaceIn');
                    }}
                    options={filterOptions}
                    title={`Filter by ${filterLabel}`}
                  />
                </div>
              )}
              {sessionFilterOptions && sessionFilterOptions.length > 0 && (
                <div className="filter sessions">
                  <MultipleSelectDropDown
                    currentSelectedValues={(query?.sessions ?? []) as string[]}
                    floatSelectedToTop
                    onChange={(selectedValues) => {
                      setQuery({ sessions: selectedValues }, 'replaceIn');
                    }}
                    options={sessionFilterOptions}
                    title="Filter by Session"
                  />
                </div>
              )}
              {tagFilterOptions && (
                <MultiSelectFilter
                  className="tagnames"
                  options={tagFilterOptions}
                  selectedFilterOption={query?.tagFilterMode}
                  selectedValues={query?.tags ?? []}
                  setSelectedFilterOption={(modeSelection) => {
                    setQuery({ tagFilterMode: modeSelection }, 'replaceIn');
                  }}
                  setSelectedValues={(newSelectedTags) => {
                    setQuery({ tags: newSelectedTags }, 'replaceIn');
                  }}
                  title="Tag Names"
                />
              )}
              {customFields.length > 0 && isCustomFieldFilterEnabled && (
                <CustomFieldFilter
                  className="mr-3"
                  customFields={customFields}
                  filterValue={
                    query.customFieldFilter ?? {
                      field: '',
                      rule: 'contains',
                    }
                  }
                  onSave={handleCustomFieldFiltersChange}
                  teamId={teamId}
                />
              )}
              <BillActionFilterDropdown
                billAction={currentActionFilter}
                setBillAction={(arg: SearchActionFilter | undefined) => {
                  if (arg) {
                    setQuery(
                      {
                        searchActionType: arg.type,
                        searchActionFromDate: arg.fromDate,
                        searchActionToDate: arg.toDate,
                      },
                      'replaceIn',
                    );
                  } else {
                    setQuery(
                      {
                        searchActionType: undefined,
                        searchActionFromDate: undefined,
                        searchActionToDate: undefined,
                      },
                      'replaceIn',
                    );
                  }
                }}
                setSearchChanged={(_b) => {}}
              />
              <BillStatusFilterDropdown
                handleStatusFiltersChange={(filters) => {
                  setQuery({ status: filters }, 'replaceIn');
                }}
                status={selectedStatuses}
                statusOptions={statusOptions}
              />
              <PartySponsorFilterDropdown
                handleSponsorFiltersChange={(filters) => {
                  setQuery({ sponsors: filters }, 'replaceIn');
                }}
                sponsorFilters={selectedSponsorFilters}
              />
              {workspaceFilterOptions && workspaceFilterOptions.length > 0 && (
                <MultiSelectFilter
                  options={workspaceFilterOptions}
                  selectedFilterOption={query?.workspaceFilterMode}
                  selectedValues={(query.workspaceIds ?? []) as string[]}
                  setSelectedFilterOption={(mode) => {
                    setQuery({ workspaceFilterMode: mode }, 'replaceIn');
                  }}
                  setSelectedValues={(selectedValues) => {
                    setQuery({ workspaceIds: selectedValues }, 'replaceIn');
                  }}
                  title="Workspaces"
                />
              )}
              <button
                className="btn btn-link text-danger p-0 d-flex align-items-center ml-3"
                onClick={clearAllFilters}
                style={{ fontSize: '12px' }}
                type="button"
              >
                <CloseIcon fill="#EB5757" />
                <span className="mx-1">Clear All</span>
              </button>
            </div>
          )}
          <div className="actions-view-group mt-3">
            {!isPublicView && (
              <BillBulkActionsDropdown
                bills={selectedRows}
                showMarkAsRead={showMarkAsReadBulkAction}
                onMarkedAsRead={clearSelectedRows}
              />
            )}
            <div className="ml-auto">
              {orgId && !readOnly && (
                <ColumnSelectDropdown
                  className="mr-2"
                  columnOptions={columnOptions}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={handleColumnChange}
                />
              )}
            </div>
            {!readOnly && (
              <div className="view-dropdown dropdown-container">
                <BillViewDropdown
                  currentView={currentView}
                  onViewChange={onViewChange}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!showFilterProperties && (
        <>
          <div className="filtered-group result-total">
            <div className="result-dropdown-container">
              <div className="total-count mb-2">
                <span>
                  {results?.count ?? 0} bill{results && results?.count == 1 ? '' : 's'}
                </span>
              </div>
              {!isPublicView && (
                <BillBulkActionsDropdown
                  bills={selectedRows}
                  showMarkAsRead={showMarkAsReadBulkAction}
                  onMarkedAsRead={clearSelectedRows}
                />
              )}
            </div>
          </div>
          <div
            className="view-dropdown dropdown-container"
            style={{ paddingLeft: '74px' }}
          >
            {headerFilterPill !== undefined ? (
              headerFilterPill
            ) : (
              <>
                <ColumnSelectDropdown
                  className="mr-2"
                  columnOptions={columnOptions}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={handleColumnChange}
                />
                <BillViewDropdown
                  currentView={currentView}
                  onViewChange={onViewChange}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BillTableHeader;
